import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, CheckCircle2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AuthShell, Logo } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function ActivateAccount() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tokenError, setTokenError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setTokenError("Link non valido"); setChecking(false); return; }
      try {
        const { data } = await api.post(`/auth/invite-info`, { token });
        setInfo(data);
      } catch (err) {
        setTokenError(formatApiError(err.response?.data?.detail) || "Link non valido");
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("La password deve contenere almeno 8 caratteri");
    if (password !== confirm) return setError("Le password non coincidono");
    setLoading(true);
    try {
      await api.post("/auth/activate", { token, password });
      setDone(true);
      toast.success("Account attivato correttamente");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="flex justify-center mb-8"><Logo size="lg" /></div>
      <Card className="rounded-2xl border-slate-200 shadow-lg shadow-slate-200/50">
        <CardContent className="p-6 sm:p-8">
          {checking ? (
            <p className="text-sm text-slate-500 text-center py-6" data-testid="activate-checking">Verifica del link…</p>
          ) : done ? (
            <div data-testid="activate-success" className="text-center">
              <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-success" />
              </div>
              <h1 className="font-heading text-xl font-bold text-slate-900">Account attivato</h1>
              <p className="text-sm text-slate-500 mt-2">
                Account attivato correttamente. Ora puoi accedere a Orbit con la tua email e la password appena scelta.
              </p>
              <Button data-testid="activate-goto-login" onClick={() => navigate("/login")}
                className="mt-5 w-full rounded-lg h-11 font-semibold">Vai al login</Button>
            </div>
          ) : tokenError ? (
            <div data-testid="activate-token-error" className="text-center">
              <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <ShieldCheck className="h-7 w-7 text-destructive" />
              </div>
              <h1 className="font-heading text-xl font-bold text-slate-900">{tokenError}</h1>
              <p className="text-sm text-slate-500 mt-2">Contatta l'ufficio per ricevere un nuovo invito di attivazione.</p>
              <Link to="/login" className="inline-block mt-5 text-sm text-primary font-medium hover:underline">Torna al login</Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <UserCheck className="h-5 w-5 text-primary" />
                <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Attiva il tuo account</h1>
              </div>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                Ciao {info?.nome} {info?.cognome}, scegli la tua password personale per completare la registrazione.
              </p>
              <div data-testid="activate-account-info" className="mb-5 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm">
                <div className="flex justify-between py-0.5"><span className="text-slate-500">Email</span><span className="font-medium text-slate-800">{info?.email}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-slate-500">Profilo</span><span className="font-medium text-slate-800">{info?.profilo_label}</span></div>
              </div>
              {error && (
                <div data-testid="activate-error"
                  className="mb-4 rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">
                  {error}
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pwd">Password</Label>
                  <div className="relative">
                    <Input id="pwd" type={show ? "text" : "password"} required value={password} className="pr-10"
                      data-testid="activate-password-input" onChange={(e) => setPassword(e.target.value)}
                      placeholder="Almeno 8 caratteri" />
                    <button type="button" onClick={() => setShow(!show)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm">Conferma password</Label>
                  <Input id="confirm" type={show ? "text" : "password"} required value={confirm}
                    data-testid="activate-confirm-input" onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Ripeti la password" />
                </div>
                <Button type="submit" disabled={loading} data-testid="activate-submit-btn"
                  className="w-full rounded-lg h-11 font-semibold transition-all duration-200 hover:brightness-110 active:scale-[0.98]">
                  {loading ? "Attivazione…" : "Attiva account"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
